import { ChangeEvent, useEffect, useState } from 'react'
import { Box, Grid, InputLabel } from '@mui/material'
import { Theme } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import { InputField, RadioGroupField } from 'src/components/shared/ui/form/fields'
import { fieldsStyles } from 'src/components/shared/ui/form/fields/fieldsStyles'
import { FormField } from 'src/enums/Form'
import { CuentaBancariaTipo, Interviniente } from 'src/interfaces/EscritosLegales'

import { NIGFieldStyles as styles } from '../../../NIG/NIGViewStyles'

const useStyles = makeStyles((theme: Theme) => ({
  smItem: {
    [theme.breakpoints.only('xs')]: {
      paddingTop: '0 !important'
    },
    [theme.breakpoints.only('lg')]: {
      paddingTop: '0 !important'
    }
  }
}))

function IBANField({
  required,
  error
}: {
  required: boolean
  error: string | undefined
}): JSX.Element {
  const { setFieldValue, values } = useFormikContext<Interviniente>()
  const [active, setActive] = useState(false)

  const props = {
    clearable: false,
    spaceLabel: false,
    spaceError: false,
    sx: styles.input,
    onFocus: () => setActive(true),
    onBlur: () => {
      setActive(false)
      const ibanContainer = document.querySelector('.iban')
      const inputs = ibanContainer?.querySelectorAll('input')

      if (inputs) {
        let newValue = ''
        inputs.forEach(input => {
          newValue += input.value
        })

        setFieldValue(
          FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA,
          newValue,
          newValue.length === 24
        )
      }
    },
    onChange: (e: ChangeEvent<HTMLInputElement>): void => {
      const { value, maxLength } = e.target
      const input = e.target
      const inputIndex = Number(input.attributes['data-attribute'].nodeValue)

      if (value.length === maxLength) {
        const ibanContainer = document.querySelector('.iban')
        const inputs = ibanContainer?.querySelectorAll('input')

        if (inputs && inputIndex < inputs?.length) {
          inputs[inputIndex].focus()
        }
      }
    },
    onPaste: (e: React.ClipboardEvent<HTMLInputElement>): void => {
      const paste = (e.clipboardData || window.Clipboard)
        .getData('text')
        ?.trim()
        ?.replaceAll(' ', '')

      if (paste?.length === 24) {
        e.preventDefault()

        paste.match(/.{1,4}/g)?.forEach((part, index) => {
          setFieldValue(`iban_${index + 1}`, part, false)
        })
      }
    }
  }

  const inputClassNames = classNames({
    required,
    error: active ? Boolean(error) : false,
    clearable: false
  })

  useEffect(() => {
    if (values[FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]) {
      const iban = values[FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]?.match(/.{1,4}/g)

      iban?.forEach((part, index) => {
        setFieldValue(`iban_${index + 1}`, part, false)
      })
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <Box style={{ display: 'none' }}>
        <InputField id={FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA} />
      </Box>
      <Box
        sx={styles.wrapper}
        data-error={Boolean(error)}
        data-active={active}
        data-disabled="false"
        className="multiField iban"
      >
        <InputLabel sx={fieldsStyles.inputLabel} className={inputClassNames}>
          {t('form.field.banco_cuenta')}
        </InputLabel>
        <Box display="flex" flexDirection="row" sx={styles.composeInput}>
          <InputField id="iban_1" maxLength={4} dataAttribute="1" {...props} />
          <Box component="span" sx={styles.separator} />
          <InputField id="iban_2" maxLength={4} dataAttribute="2" {...props} />
          <Box component="span" sx={styles.separator} />
          <InputField id="iban_3" maxLength={4} dataAttribute="3" {...props} />
          <Box component="span" sx={styles.separator} />
          <InputField id="iban_4" maxLength={4} dataAttribute="4" {...props} />
          <Box component="span" sx={styles.separator} />
          <InputField id="iban_5" maxLength={4} dataAttribute="5" {...props} />
          <Box component="span" sx={styles.separator} />
          <InputField id="iban_6" maxLength={4} dataAttribute="6" {...props} />
        </Box>
        {error && !active && (
          <Box component="span" sx={fieldsStyles.helpText}>
            {error}
          </Box>
        )}
      </Box>
    </>
  )
}

export default function BankAccount(): JSX.Element {
  const { errors, values, setFieldValue, validateField } = useFormikContext<Interviniente>()
  const bankAccountType = values[FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO]
  const classes = useStyles()

  useEffect(() => {
    validateField(FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA)
  }, [bankAccountType, validateField])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InfoBar
          message={t('form.warning.bank_account')}
          type={AlertType.INFO}
          showCloseIcon={false}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={4} order={1}>
        {values[FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO] === CuentaBancariaTipo.SWIFT ? (
          <InputField
            id={FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA}
            label={t('form.field.banco_cuenta')}
            required={values[FormField.INTERVINIENTES_INTERVENCION_TIPO]?.codigo === 'SOL'}
            maxLength={40}
          />
        ) : (
          <IBANField
            required={values[FormField.INTERVINIENTES_INTERVENCION_TIPO]?.codigo === 'SOL'}
            error={errors[FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6} lg={4} order={{ lg: 2, sm: 3, xs: 3 }}>
        <InputField
          id={FormField.INTERVINIENTE_CUENTA_BANCARIA_TITULAR}
          label={t('form.field.banco_titular')}
          required={values[FormField.INTERVINIENTES_INTERVENCION_TIPO]?.codigo === 'SOL'}
          maxLength={200}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={4} order={{ lg: 3, sm: 4, xs: 4 }}>
        <InputField
          id={FormField.INTERVINIENTE_CUENTA_BANCARIA_NIF}
          label={t('form.field.banco_nif')}
          required={values[FormField.INTERVINIENTES_INTERVENCION_TIPO]?.codigo === 'SOL'}
          maxLength={14}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        lg={4}
        flexDirection="row"
        display="flex"
        flexWrap="nowrap"
        order={{ lg: 4, sm: 2, xs: 2 }}
        className={classes.smItem}
      >
        <RadioGroupField
          id={FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO}
          fieldName={FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO}
          items={[
            { label: t('form.field.banco_iban'), value: CuentaBancariaTipo.IBAN },
            { label: t('form.field.banco_swift'), value: CuentaBancariaTipo.SWIFT }
          ]}
          value={values[FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO]}
          getValue={value => {
            setFieldValue(FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO, Number(value))
          }}
        />
      </Grid>
    </Grid>
  )
}
